import { WaterLevelColorConfig, RainfallColorConfig, WaterLevelDermagaColorConfig } from '../types/waterLevel';

// Water Level color configurations based on quantity ranges (matching getBlockColor in WaterLevel.tsx)
export const WATER_LEVEL_COLORS: WaterLevelColorConfig[] = [
    { min: 0, max: 0, color: "#A0AEC0", label: "Tergenang (0cm)" },
    { min: 1, max: 29, color: "#204cddff", label: "Kelebihan Air (1-29cm)" },
    { min: 30, max: 49, color: "#02C902", label: "Cukup (30-49cm)" },
    { min: 50, max: 60, color: "#FFD700", label: "Kurang Air (50-60cm)" },
    { min: 61, max: 1000, color: "#FF0000", label: "Sangat Kurang Air (>60cm)" },
];

// Water Level color configurations for Dry Season (Kemarau)
export const WATER_LEVEL_KEMARAU_COLORS: WaterLevelColorConfig[] = [
    { min: 0, max: 0, color: "#A0AEC0", label: "Tergenang (<1cm)" },
    { min: 1, max: 19, color: "#204cddff", label: "Kelebihan Air (1-19cm)" },
    { min: 20, max: 39, color: "#02C902", label: "Cukup (20-39cm)" },
    { min: 40, max: 49, color: "#FFD700", label: "Kurang Air (40-49cm)" },
    { min: 50, max: 1000, color: "#FF0000", label: "Sangat Kurang Air (>=50cm)" },
];

// Water Level Dermaga color configurations
export const WATER_LEVEL_DERMAGA_COLORS: WaterLevelDermagaColorConfig[] = [
    { min: 0, max: 54, color: "#FF0000", label: "Surut Extreme (0-54cm)" },
    { min: 55, max: 144, color: "#FFD700", label: "Surut (55-144cm)" },
    { min: 145, max: 216, color: "#02C902", label: "Normal (145-216cm)" },
    { min: 217, max: 305, color: "#204cddff", label: "Pasang (217-305cm)" },
    { min: 306, max: 360, color: "#FF0000", label: "Pasang Extreme (306-360cm)" },
];

// Rainfall color configurations based on quantity ranges (matching getBlockColor in WaterLevel.tsx)
export const RAINFALL_COLORS: RainfallColorConfig[] = [
    { min: 0, max: 0, color: "#A0AEC0", label: "Tidak Hujan (0mm)" },
    { min: 1, max: 20, color: "#02C902", label: "Hujan Ringan (1-20mm)" },
    { min: 21, max: 50, color: "#FFD700", label: "Hujan Sedang (21-50mm)" },
    { min: 51, max: 100, color: "#204cddff", label: "Hujan Lebat (51-100mm)" },
    { min: 101, max: 150, color: "#FF0000", label: "Hujan Sangat Lebat (101-150mm)" },
    { min: 151, max: 1000, color: "#923ee5ff", label: "Hujan Ekstrim (>150mm)" },
];

export const getWaterLevelColor = (quantity: number, season: 'normal' | 'kemarau' = 'normal'): string => {
    if (season === 'kemarau') {
        if (quantity >= 50) return '#FF0000'; // Sangat Kurang Air - red
        if (quantity >= 40) return '#FFD700'; // Kurang Air - yellow
        if (quantity >= 20) return '#02C902'; // Cukup - green
        if (quantity >= 1) return '#204cddff'; // Kelebihan Air - blue
        return '#A0AEC0'; // Tergenang (0) - gray
    }

    // Default Normal Season logic
    if (quantity > 60) return '#FF0000'; // Sangat Kurang Air - red
    if (quantity >= 50) return '#FFD700'; // Kurang Air - yellow
    if (quantity >= 30) return '#02C902'; // Cukup - green
    if (quantity >= 1) return '#204cddff'; // Kelebihan Air - blue
    return '#A0AEC0'; // Tergenang (0) - gray
};

export const getWaterLevelDermagaColor = (quantity: number): string => {
    // Use same logic as getBlockColor in WaterLevel.tsx for water_level_dermaga
    if (quantity >= 306) return '#FF0000'; // Pasang Extreme - red
    if (quantity >= 217) return '#204cddff'; // Pasang - blue
    if (quantity >= 145) return '#02C902'; // Normal - green
    if (quantity >= 55) return '#FFD700'; // Surut - yellow
    return '#FF0000'; // Surut Extreme (0-54) - red
};

export const getRainfallColor = (quantity: number): string => {
    // Use same logic as getBlockColor in WaterLevel.tsx for curah_hujan
    if (quantity > 150) return '#923ee5ff'; // Hujan Ekstrim - purple
    if (quantity >= 101) return '#FF0000'; // Hujan Sangat Lebat - red
    if (quantity >= 51) return '#204cddff'; // Hujan Lebat - blue
    if (quantity >= 21) return '#FFD700'; // Hujan Sedang - yellow
    if (quantity >= 1) return '#02C902'; // Hujan Ringan - green
    return '#A0AEC0'; // Tidak hujan - gray
};

export const getWaterLevelConfig = (quantity: number, season: 'normal' | 'kemarau' = 'normal'): WaterLevelColorConfig | undefined => {
    if (season === 'kemarau') {
        if (quantity >= 50) return { min: 50, max: 1000, color: '#FF0000', label: 'Sangat Kurang Air (>=50cm)' };
        if (quantity >= 40) return { min: 40, max: 49, color: '#FFD700', label: 'Kurang Air (40-49cm)' };
        if (quantity >= 20) return { min: 20, max: 39, color: '#02C902', label: 'Cukup (20-39cm)' };
        if (quantity >= 1) return { min: 1, max: 19, color: '#204cddff', label: 'Kelebihan Air (1-19cm)' };
        return { min: 0, max: 0, color: '#A0AEC0', label: 'Tergenang (<1cm)' };
    }

    if (quantity > 60) return { min: 61, max: 1000, color: '#FF0000', label: 'Sangat Kurang Air (>60cm)' };
    if (quantity >= 50) return { min: 50, max: 60, color: '#FFD700', label: 'Kurang Air (50-60cm)' };
    if (quantity >= 30) return { min: 30, max: 49, color: '#02C902', label: 'Cukup (30-49cm)' };
    if (quantity >= 1) return { min: 1, max: 29, color: '#204cddff', label: 'Kelebihan Air (1-29cm)' };
    return { min: 0, max: 0, color: '#A0AEC0', label: 'Tergenang (0cm)' };
};

export const getWaterLevelDermagaConfig = (quantity: number): WaterLevelDermagaColorConfig | undefined => {
    // Use same logic as getBlockColor
    if (quantity >= 306) return { min: 306, max: 360, color: '#FF0000', label: 'Pasang Extreme (306-360cm)' };
    if (quantity >= 217) return { min: 217, max: 305, color: '#204cddff', label: 'Pasang (217-305cm)' };
    if (quantity >= 145) return { min: 145, max: 216, color: '#02C902', label: 'Normal (145-216cm)' };
    if (quantity >= 55) return { min: 55, max: 144, color: '#FFD700', label: 'Surut (55-144cm)' };
    return { min: 0, max: 54, color: '#FF0000', label: 'Surut Extreme (0-54cm)' };
};

export const getRainfallConfig = (quantity: number): RainfallColorConfig | undefined => {
    // Use same logic as getBlockColor
    if (quantity > 150) return { min: 151, max: 1000, color: '#923ee5ff', label: 'Hujan Ekstrim (>150mm)' };
    if (quantity >= 101) return { min: 101, max: 150, color: '#FF0000', label: 'Hujan Sangat Lebat (101-150mm)' };
    if (quantity >= 51) return { min: 51, max: 100, color: '#204cddff', label: 'Hujan Lebat (51-100mm)' };
    if (quantity >= 21) return { min: 21, max: 50, color: '#FFD700', label: 'Hujan Sedang (21-50mm)' };
    if (quantity >= 1) return { min: 1, max: 20, color: '#02C902', label: 'Hujan Ringan (1-20mm)' };
    return { min: 0, max: 0, color: '#A0AEC0', label: 'Tidak Hujan (0mm)' };
};

export const formatDateTime = (dateTimeString: string): { date: string; time: string } => {
    const date = new Date(dateTimeString);
    const dateStr = date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    const timeStr = date.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });

    return { date: dateStr, time: timeStr };
};

export const getTodayDateString = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
};

export const getDateNDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
};

export const getCurrentSeason = (): 'normal' | 'kemarau' => {
    const currentMonth = new Date().getMonth() + 1; // getMonth() is 0-indexed
    // April (4) to October (10) is Kemarau
    if (currentMonth >= 4 && currentMonth <= 10) {
        return 'kemarau';
    }
    return 'normal';
};

// Get color based on dashboard type and quantity
export const getColorByDashboardType = (
    dashboardType: 'water_level' | 'curah_hujan' | 'water_level_dermaga',
    quantity: number,
    season: 'normal' | 'kemarau' = 'normal'
): string => {
    if (dashboardType === 'water_level') {
        return getWaterLevelColor(quantity, season);
    } else if (dashboardType === 'water_level_dermaga') {
        return getWaterLevelDermagaColor(quantity);
    } else {
        return getRainfallColor(quantity);
    }
};

// Get label based on dashboard type and quantity
export const getLabelByDashboardType = (
    dashboardType: 'water_level' | 'curah_hujan' | 'water_level_dermaga',
    quantity: number,
    season: 'normal' | 'kemarau' = 'normal'
): string => {
    if (dashboardType === 'water_level') {
        const config = getWaterLevelConfig(quantity, season);
        return config?.label || 'Unknown';
    } else if (dashboardType === 'water_level_dermaga') {
        const config = getWaterLevelDermagaConfig(quantity);
        return config?.label || 'Unknown';
    } else {
        const config = getRainfallConfig(quantity);
        return config?.label || 'Unknown';
    }
};

export const hexToColorScheme = (hex: string): string => {
    switch (hex.toLowerCase()) {
        case '#FF0000': return 'red';
        case '#f1f429ff': return 'yellow';
        case '#02C902': return 'green';
        case '#204cddff': return 'blue';
        case '#a0aec0': return 'gray';
        case '#923ee5ff': return 'purple';
        default: return 'gray'; // fallback
    }
};
